import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wind, Anchor, ArrowLeft, RotateCcw, Waves, Heart, Sparkles, Activity,
  Check, Timer, Sun, Brain, ScanLine, HandHeart, Cloud, Repeat2,
} from "lucide-react";
import { track } from "@/lib/api";
import { TOOLS } from "@/constants/testIds";
import { MicButton } from "@/components/MicButton";

const P = "?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

const GROUNDING = [
  { head: "5 things you can see", body: "Look around and name five, slowly." },
  { head: "4 things you can feel", body: "The chair, your feet, the air on your skin." },
  { head: "3 things you can hear", body: "Near sounds and far sounds." },
  { head: "2 things you can smell", body: "Or two scents you like." },
  { head: "1 thing you can taste", body: "Or one thing you're grateful for." },
];

const PMR = [
  { head: "Hands & forearms", body: "Make tight fists. Hold… now let them fall open and soft." },
  { head: "Shoulders", body: "Lift them to your ears. Hold… now drop them all the way down." },
  { head: "Face & jaw", body: "Scrunch your whole face. Hold… now smooth and release." },
  { head: "Chest & belly", body: "Take a breath and tense your middle. Hold… now exhale and loosen." },
  { head: "Legs & thighs", body: "Press your legs together and tighten. Hold… now let them go heavy." },
  { head: "Feet & toes", body: "Curl your toes down. Hold… now release and feel them rest." },
];

const COMPASSION = [
  { head: "This is a moment of difficulty.", body: "Name it gently — 'this is hard right now.' Just noticing, without judging." },
  { head: "Struggle is part of being human.", body: "Every woman you admire has hard moments too. You are not alone in this." },
  { head: "May I be kind to myself.", body: "Place a hand on your heart. Offer yourself the warmth you give everyone else." },
];

const BODYSCAN = [
  { head: "Feet & legs", body: "Bring attention to your feet and legs. Notice any sensation, and let them soften." },
  { head: "Hips & belly", body: "Move your awareness up. Let your belly rise and fall, releasing any holding." },
  { head: "Chest & back", body: "Notice your chest and back. Let your breath widen them gently." },
  { head: "Hands & arms", body: "Feel your hands and arms grow heavy and warm." },
  { head: "Shoulders & neck", body: "Let your shoulders drop and your neck lengthen." },
  { head: "Face & head", body: "Soften your jaw, your eyes, your forehead." },
  { head: "Whole body", body: "Feel your whole body at once, resting and supported." },
];

const RAIN = [
  { head: "Recognise", body: "What is happening inside you right now? Name the feeling — 'anxiety,' 'hurt,' 'anger.'" },
  { head: "Allow", body: "Let the feeling be here, without fixing or pushing it away. Just for this moment." },
  { head: "Investigate", body: "Where do you feel it in your body? What does this part of you most need?" },
  { head: "Nurture", body: "Offer yourself what you need — a kind word, a hand on your heart, a slow breath." },
];

const METTA = [
  { head: "For yourself", body: "Silently: 'May I be safe. May I be well. May I be at ease.'" },
  { head: "For someone you love", body: "Picture them. 'May you be safe. May you be well. May you be at ease.'" },
  { head: "For someone neutral", body: "Someone you barely know. Offer them the same warm wishes." },
  { head: "For everyone", body: "Widen it out: 'May all of us be safe, well, and at ease.'" },
];

const STOP = [
  { head: "S — Stop", body: "Whatever you're doing, pause for a moment. Just stop." },
  { head: "T — Take a breath", body: "One slow breath, all the way in and all the way out." },
  { head: "O — Observe", body: "Notice your thoughts, feelings, and body — without judging them." },
  { head: "P — Proceed", body: "Choose your next step on purpose, from calm rather than reaction." },
];

const REFRAME = [
  { head: "The trigger", body: "What happened? Name the situation in one plain sentence." },
  { head: "The fact", body: "Strip out interpretation. What can you actually observe — just the facts?" },
  { head: "The story", body: "What story did your mind add? ('She's upset with me,' 'I always fail.')" },
  { head: "A truer story", body: "What's another explanation that fits the same facts? Widen the lens." },
  { head: "What now", body: "From this truer story, what's one calmer next step you can take?" },
];

const BUTTERFLY = [
  { head: "Cross your arms", body: "Cross your arms over your chest, hands resting on opposite shoulders." },
  { head: "Tap slowly, side to side", body: "Gently tap left, right, left, right — slow and steady, like a heartbeat." },
  { head: "Breathe and continue", body: "Keep tapping for 30–60 seconds, breathing slowly. Let your body settle." },
];

function BackBtn({ onExit }) {
  return (
    <button onClick={onExit} className="flex items-center gap-2 text-[#59535B] hover:text-[#252326] mb-8">
      <ArrowLeft className="h-4 w-4" /> Back to tools
    </button>
  );
}

function Breathing({ config, onExit }) {
  const { phases, title, hint, testid } = config;
  const [i, setI] = useState(0);
  const [cycles, setCycles] = useState(0);
  const timer = useRef(null);
  useEffect(() => {
    timer.current = setTimeout(() => {
      setI((prev) => {
        const next = (prev + 1) % phases.length;
        if (next === 0) setCycles((c) => c + 1);
        return next;
      });
    }, phases[i].ms);
    return () => clearTimeout(timer.current);
  }, [i, phases]);
  return (
    <div className="flex flex-col items-center justify-center py-10" data-testid={testid}>
      <div className="self-start"><BackBtn onExit={onExit} /></div>
      <div className="relative h-72 w-72 flex items-center justify-center">
        <motion.div className="absolute rounded-full bg-[#C89B55]/15"
          animate={{ scale: phases[i].scale }} transition={{ duration: phases[i].ms / 1000, ease: "easeInOut" }}
          style={{ height: 180, width: 180 }} />
        <motion.div className="absolute rounded-full bg-[#C89B55]/25"
          animate={{ scale: phases[i].scale }} transition={{ duration: phases[i].ms / 1000, ease: "easeInOut" }}
          style={{ height: 130, width: 130 }} />
        <div className="relative h-28 w-28 rounded-full bg-[#291B2D] flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.span key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="font-display text-lg text-[#FCFAF6] text-center px-2">{phases[i].label}</motion.span>
          </AnimatePresence>
        </div>
      </div>
      <p className="text-sm text-[#8A838E] mt-8">{title} · {cycles} {cycles === 1 ? "round" : "rounds"} complete</p>
      <p className="text-[#59535B] mt-2 max-w-sm text-center">{hint}</p>
    </div>
  );
}

function StepTool({ config, onExit }) {
  const { steps, title, done, testid } = config;
  const [step, setStep] = useState(0);
  const finished = step >= steps.length;
  return (
    <div className="py-10 max-w-lg mx-auto" data-testid={testid}>
      <BackBtn onExit={onExit} />
      {finished ? (
        <div className="text-center">
          <div className="h-16 w-16 rounded-full bg-[#ECF3EE] text-[#3D6B52] flex items-center justify-center mx-auto mb-5">
            <Check className="h-7 w-7" />
          </div>
          <h2 className="font-display text-2xl font-semibold">{done.title}</h2>
          <p className="text-[#59535B] mt-2">{done.text}</p>
          <button onClick={() => setStep(0)} className="mt-6 rounded-full bg-[#291B2D] text-[#FCFAF6] px-6 py-3 font-medium flex items-center gap-2 mx-auto">
            <RotateCcw className="h-4 w-4" /> Again
          </button>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">{title} · {step + 1} of {steps.length}</p>
            <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-3">{steps[step].head}</h2>
            <p className="text-[#59535B] mt-3 leading-relaxed">{steps[step].body}</p>
            <button data-testid={TOOLS.stepNext} onClick={() => setStep((s) => s + 1)}
              className="mt-8 rounded-full bg-[#C89B55] text-[#291B2D] hover:bg-[#B38743] px-8 py-3.5 font-semibold transition-all">
              {step === steps.length - 1 ? "Finish" : "Next"}
            </button>
            <div className="flex justify-center gap-2 mt-8">
              {steps.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i <= step ? "w-6 bg-[#C89B55]" : "w-1.5 bg-[#E5DED4]"}`} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

function ThreeGoodThings({ onExit }) {
  const [items, setItems] = useState(["", "", ""]);
  const [saved, setSaved] = useState(false);
  const set = (i, v) => setItems((arr) => arr.map((x, idx) => (idx === i ? v : x)));
  const filled = items.filter((x) => x.trim()).length;
  return (
    <div className="py-10 max-w-lg mx-auto" data-testid="tools-gratitude">
      <BackBtn onExit={onExit} />
      {saved ? (
        <div className="text-center">
          <div className="h-16 w-16 rounded-full bg-[#F8F0DF] text-[#9A7526] flex items-center justify-center mx-auto mb-5">
            <Sparkles className="h-7 w-7" />
          </div>
          <h2 className="font-display text-2xl font-semibold">Held, and noticed.</h2>
          <p className="text-[#59535B] mt-2">Naming what went well trains your mind to find more of it. Well done.</p>
          <button onClick={() => { setItems(["", "", ""]); setSaved(false); }}
            className="mt-6 rounded-full bg-[#291B2D] text-[#FCFAF6] px-6 py-3 font-medium flex items-center gap-2 mx-auto">
            <RotateCcw className="h-4 w-4" /> Again
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Three good things</p>
          <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-1">What went well today?</h2>
          <p className="text-[#59535B] mt-2">Name three — big or tiny. A kind word, a good coffee, a hard thing you handled.</p>
          <div className="space-y-4 mt-6">
            {items.map((v, i) => (
              <div key={i} className="relative">
                <span className="absolute left-4 top-3.5 font-display text-[#C89B55] font-semibold">{i + 1}</span>
                <textarea value={v} onChange={(e) => set(i, e.target.value)} rows={2}
                  data-testid={`tools-gratitude-input-${i}`}
                  placeholder="Something good, and why it mattered…"
                  className="w-full resize-none rounded-2xl border border-[#E5DED4] bg-white pl-10 pr-14 py-3 outline-none focus:ring-2 focus:ring-[#C89B55] text-[#252326]" />
                <MicButton testid={`tools-gratitude-dictate-${i}`} className="absolute bottom-2.5 right-2.5 h-9 w-9"
                  onAppend={(t) => setItems((arr) => arr.map((x, idx) => (idx === i ? (x ? x + " " + t : t) : x)))} />
              </div>
            ))}
          </div>
          <button data-testid={TOOLS.gratitudeSave} disabled={filled === 0}
            onClick={() => { setSaved(true); track("tool_gratitude_done", { count: filled }); }}
            className="mt-6 w-full rounded-full bg-[#C89B55] text-[#291B2D] hover:bg-[#B38743] disabled:opacity-40 px-8 py-3.5 font-semibold transition-all">
            Hold these {filled ? `(${filled})` : ""}
          </button>
        </>
      )}
    </div>
  );
}

const IMG = {
  breathing: "https://images.unsplash.com/photo-1565210188646-68318a19c558",
  sigh: "https://images.unsplash.com/photo-1549627007-bb4536763996",
  fourSevenEight: "https://images.unsplash.com/photo-1562445455-04734c5504b3",
  coherent: "https://images.unsplash.com/photo-1518837695005-2083093ee35b",
  grounding: "https://images.unsplash.com/photo-1758135986608-aa6352d75b06",
  bodyscan: "https://images.unsplash.com/photo-1649888381057-d5d03c105f82",
  pmr: "https://images.unsplash.com/photo-1764111586096-f59a1376b9ac",
  compassion: "https://images.unsplash.com/photo-1774773134239-df5d22ab1e59",
  rain: "https://images.unsplash.com/photo-1606471059439-2dfc9bbfb150",
  lovingkindness: "https://images.unsplash.com/photo-1661181296439-23d021597c27",
  stop: "https://images.unsplash.com/photo-1468581264429-2548ef9eb732",
  reframe: "https://images.unsplash.com/photo-1630313626009-26da82467f8c",
  gratitude: "https://images.unsplash.com/photo-1630313621450-9e90dcf99d09",
  butterfly: "https://images.unsplash.com/photo-1731461298531-9568f2351862",
};

const BREATH_CONFIGS = {
  breathing: {
    testid: "tools-breathing", title: "Box breathing",
    hint: "Follow the circle. In for four, hold, out for four, hold. Let your shoulders drop.",
    phases: [
      { label: "Breathe in", ms: 4000, scale: 1.4 },
      { label: "Hold", ms: 4000, scale: 1.4 },
      { label: "Breathe out", ms: 4000, scale: 1 },
      { label: "Hold", ms: 4000, scale: 1 },
    ],
  },
  sigh: {
    testid: "tools-sigh", title: "Physiological sigh",
    hint: "Two breaths in through the nose, then a long, slow breath out through the mouth. A few rounds calms the body fast.",
    phases: [
      { label: "Inhale", ms: 2500, scale: 1.25 },
      { label: "Second sip in", ms: 1500, scale: 1.45 },
      { label: "Long exhale", ms: 6000, scale: 1 },
    ],
  },
  fourSevenEight: {
    testid: "tools-478", title: "4-7-8 breathing",
    hint: "In through the nose for four, hold for seven, out through the mouth for eight. A natural tranquiliser for the nervous system.",
    phases: [
      { label: "Breathe in · 4", ms: 4000, scale: 1.4 },
      { label: "Hold · 7", ms: 7000, scale: 1.4 },
      { label: "Breathe out · 8", ms: 8000, scale: 1 },
    ],
  },
  coherent: {
    testid: "tools-coherent", title: "Coherent breathing",
    hint: "Slow, even breaths — in for about six, out for about six. This ~5 breaths a minute balances your nervous system.",
    phases: [
      { label: "Breathe in", ms: 5500, scale: 1.4 },
      { label: "Breathe out", ms: 5500, scale: 1 },
    ],
  },
};

const STEP_CONFIGS = {
  grounding: { testid: "tools-grounding", title: "5-4-3-2-1", steps: GROUNDING, done: { title: "You're back in the room.", text: "Notice your breath. You did that gently and well." } },
  bodyscan: { testid: "tools-bodyscan", title: "Body scan", steps: BODYSCAN, done: { title: "Settled, head to toe.", text: "Carry this softness with you into whatever comes next." } },
  pmr: { testid: "tools-pmr", title: "Muscle relaxation", steps: PMR, done: { title: "Softer, all the way through.", text: "Notice the difference between tense and released." } },
  compassion: { testid: "tools-compassion", title: "Self-compassion", steps: COMPASSION, done: { title: "Kinder, to yourself.", text: "You offered yourself what you so freely give others." } },
  rain: { testid: "tools-rain", title: "RAIN", steps: RAIN, done: { title: "Met with care.", text: "You turned toward a hard feeling instead of away. That's real courage." } },
  lovingkindness: { testid: "tools-metta", title: "Loving-kindness", steps: METTA, done: { title: "Warmth, sent outward.", text: "Wishing others well quietly warms you too." } },
  stop: { testid: "tools-stop", title: "STOP", steps: STOP, done: { title: "Back in choice.", text: "One pause can change the whole shape of a reaction." } },
  reframe: { testid: "tools-reframe", title: "Reframe the thought", steps: REFRAME, done: { title: "A wider lens.", text: "Most anxiety lives in the story, not the fact. You just loosened it." } },
  butterfly: { testid: "tools-butterfly", title: "Butterfly hug", steps: BUTTERFLY, done: { title: "Soothed, gently.", text: "This slow, side-to-side tapping helps your body feel safe again." } },
};

export default function Tools() {
  const [active, setActive] = useState(null);
  useEffect(() => { track("tools_viewed"); }, []);

  if (BREATH_CONFIGS[active]) return <Breathing config={BREATH_CONFIGS[active]} onExit={() => setActive(null)} />;
  if (STEP_CONFIGS[active]) return <StepTool config={STEP_CONFIGS[active]} onExit={() => setActive(null)} />;
  if (active === "gratitude") return <ThreeGoodThings onExit={() => setActive(null)} />;

  const tools = [
    { key: "breathing", icon: Wind, title: "Box breathing", text: "A guided 4-4-4-4 breath to steady a racing mind in minutes.", tag: "Anxiety · Focus", fg: "#9A7526" },
    { key: "sigh", icon: Waves, title: "Physiological sigh", text: "A double inhale and long exhale — the fastest way to calm your nervous system.", tag: "Stress · Fast reset", fg: "#3E6B84" },
    { key: "fourSevenEight", icon: Timer, title: "4-7-8 breathing", text: "In 4, hold 7, out 8 — a natural tranquiliser that eases you toward sleep.", tag: "Sleep · Calm", fg: "#7B628B" },
    { key: "coherent", icon: Sun, title: "Coherent breathing", text: "Slow, even breaths at five a minute to balance your whole system.", tag: "Balance · Heart", fg: "#3D6B52" },
    { key: "grounding", icon: Anchor, title: "5-4-3-2-1 grounding", text: "Come back to the present when anxiety or overwhelm pulls you away.", tag: "Panic · Overwhelm", fg: "#3D6B52" },
    { key: "bodyscan", icon: ScanLine, title: "Body scan", text: "Move attention slowly through the body to release held tension.", tag: "Tension · Sleep", fg: "#7B628B" },
    { key: "pmr", icon: Activity, title: "Muscle relaxation", text: "Tense and release each muscle group to melt physical stress.", tag: "Tension · Sleep", fg: "#7B628B" },
    { key: "compassion", icon: Heart, title: "Self-compassion break", text: "Three gentle steps to soften self-criticism and meet yourself with kindness.", tag: "Self-criticism", fg: "#B97978" },
    { key: "rain", icon: Cloud, title: "RAIN for hard feelings", text: "Recognise, Allow, Investigate, Nurture — a way to sit with a difficult emotion.", tag: "Big emotions", fg: "#3E6B84" },
    { key: "lovingkindness", icon: HandHeart, title: "Loving-kindness", text: "Send warm wishes to yourself and others — a practice that lifts connection.", tag: "Connection · Mood", fg: "#B97978" },
    { key: "stop", icon: Anchor, title: "STOP reset", text: "Stop, Take a breath, Observe, Proceed — interrupt a reaction in the moment.", tag: "Reactivity", fg: "#9A7526" },
    { key: "reframe", icon: Brain, title: "Reframe the thought", text: "Separate fact from story and find a truer, calmer way to see it.", tag: "Worry · CBT", fg: "#7B628B" },
    { key: "gratitude", icon: Sparkles, title: "Three good things", text: "Name what went well today — a proven practice that lifts mood over time.", tag: "Mood · Positivity", fg: "#9A7526" },
    { key: "butterfly", icon: Repeat2, title: "Butterfly hug", text: "Slow, alternating self-taps to soothe your body when you feel shaken.", tag: "Soothing · Trauma", fg: "#B97978" },
  ];

  return (
    <div className="space-y-8" data-testid={TOOLS.page}>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Tools</p>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-1">A moment of calm</h1>
        <p className="text-[#59535B] mt-2 max-w-xl">Quick, science-backed practices for the moments you need to reset — anxiety, tension, overwhelm, hard feelings, or a heavy day.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {tools.map((t) => (
          <motion.button key={t.key} data-testid={`${TOOLS.card}-${t.key}`} onClick={() => setActive(t.key)}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="text-left bg-white rounded-3xl overflow-hidden border border-[#291B2D]/5 shadow-[0_4px_20px_rgba(41,27,45,0.04)] transition-all hover:-translate-y-0.5 hover:shadow-md flex flex-col">
            <div className="relative h-36 overflow-hidden bg-[#F7F2EA]">
              <img src={IMG[t.key] + P} alt="" loading="lazy" className="h-full w-full object-cover" style={{ objectPosition: "50% 40%" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#291B2D]/45 to-transparent" />
              <span className="absolute bottom-3 left-4 h-11 w-11 rounded-2xl bg-white/90 backdrop-blur flex items-center justify-center">
                <t.icon className="h-5 w-5" style={{ color: t.fg }} strokeWidth={1.9} />
              </span>
            </div>
            <div className="p-6 flex flex-col flex-1">
              <h3 className="font-display text-lg font-semibold">{t.title}</h3>
              <p className="text-[#59535B] text-sm mt-2 leading-relaxed flex-1">{t.text}</p>
              <span className="mt-4 inline-block self-start text-[11px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full"
                style={{ backgroundColor: t.fg + "18", color: t.fg }}>{t.tag}</span>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
